import { useEffect, useMemo, useRef, useState } from "react";
import { useAhaCelebration } from "@/contexts/AhaCelebrationContext";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  MapPin,
  RefreshCw,
  Radar as RadarIcon,
  MessageCircle,
  Star,
  QrCode,
  ScanLine,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useMatches,
  useSetLocation,
  getBrowserLocation,
  formatDistance,
  timeAgo,
} from "@/lib/radar";
import {
  useSavedMatches,
  useSaveMatch,
  useUnsaveMatch,
} from "@/lib/savedMatches";
import { useCreateThread } from "@/lib/chat";
import { formatApiErrorDetail } from "@/lib/api";
import MatchDetailsDialog from "@/components/MatchDetailsDialog";
import TradeGenerateModal from "@/components/TradeGenerateModal";
import TradeScanModal from "@/components/TradeScanModal";
import { useI18n } from "@/lib/I18nContext";

const VISIBLE_CHIPS = 8;

export default function Radar() {
  const { t } = useI18n();
  const matches = useMatches();
  const saved = useSavedMatches();
  const setLocation = useSetLocation();
  const saveMatch = useSaveMatch();
  const unsaveMatch = useUnsaveMatch();
  const createThread = useCreateThread();
  const navigate = useNavigate();

  const [geoError, setGeoError] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeMatch, setActiveMatch] = useState(null);
  const [viewTab, setViewTab] = useState("all"); // "all" | "saved"
  const [tradeTarget, setTradeTarget] = useState(null); // {user_id, display_name} | null
  const [scanOpen, setScanOpen] = useState(false);
  const seenUpdateKeysRef = useRef(new Set());
  const { triggerAhaCheck } = useAhaCelebration();

  const hasLocation = matches.isSuccess;
  const locationRequired =
    matches.isError && matches.error?.code === "location_required";

  const savedIds = useMemo(
    () => new Set((saved.data || []).map((s) => s.user_id)),
    [saved.data]
  );

  // Fire toasts for saved_match_updates — once per (user_id|type|counts) tuple
  useEffect(() => {
    const updates = matches.data?.saved_match_updates;
    if (!updates || !updates.length) return;
    for (const u of updates) {
      const key = `${u.user_id}|${u.type}|${u.give_count}|${u.get_count}`;
      if (seenUpdateKeysRef.current.has(key)) continue;
      seenUpdateKeysRef.current.add(key);
      const msg = formatSavedUpdate(u);
      toast.success(msg, { duration: 4500 });
    }
  }, [matches.data?.saved_match_updates]);

  // Phase 7 (FiguTokens): trigger aha celebration on first successful match
  // fetch with >=1 mutual match. Server dedupes; this is a no-op afterwards.
  useEffect(() => {
    const list = matches.data?.matches;
    if (list && list.length > 0) {
      triggerAhaCheck("first_match");
    }
  }, [matches.data?.matches, triggerAhaCheck]);

  async function captureAndSave() {
    setGeoError("");
    setBusy(true);
    try {
      const { latitude, longitude } = await getBrowserLocation();
      await setLocation.mutateAsync({ latitude, longitude });
      toast.success("Location updated · scanning for trades");
    } catch (err) {
      setGeoError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleSave(userId, isCurrentlySaved) {
    try {
      if (isCurrentlySaved) {
        await unsaveMatch.mutateAsync(userId);
        toast.success("Removed from saved matches");
      } else {
        await saveMatch.mutateAsync(userId);
        toast.success("Saved — we'll notify you when trades shift");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function handleOpenChat(userId) {
    try {
      const t = await createThread.mutateAsync({ other_user_id: userId });
      navigate(`/app/chat/${t.thread_id}`);
    } catch (e) {
      if (e.code === "not_a_match") {
        toast.error("This user isn't a current match.");
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    }
  }

  // Build the rows to render based on the active tab
  const rows = useMemo(() => {
    if (viewTab === "saved") {
      // Use saved data; merge distance/give/get from current matches when available
      const list = saved.data || [];
      return list.map((s) => ({
        user_id: s.user_id,
        display_name: s.display_name,
        is_current_match: s.is_current_match,
        distance_meters: s.distance_meters,
        give:
          (matches.data?.matches || []).find((m) => m.user_id === s.user_id)
            ?.give || [],
        get:
          (matches.data?.matches || []).find((m) => m.user_id === s.user_id)
            ?.get || [],
        give_count: s.give_count,
        get_count: s.get_count,
      }));
    }
    return (matches.data?.matches || []).map((m) => ({
      ...m,
      is_current_match: true,
    }));
  }, [viewTab, saved.data, matches.data]);

  return (
    <div data-testid="radar-page" className="space-y-8 max-w-3xl mx-auto">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight">
            {t("radar.title")}
          </h1>
          <p className="font-body text-sm text-[#A1A1AA] mt-1">
            {t("radar.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="radar-scan-trade-button"
            onClick={() => setScanOpen(true)}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-[#E5B83A]/30 bg-[#E5B83A]/5 text-[#E5B83A] font-body text-xs hover:bg-[#E5B83A]/10 transition-colors"
          >
            <ScanLine className="h-3.5 w-3.5" />
            {t("trade.scan_qr")}
          </button>
          <LocationChip
            hasLocation={hasLocation}
            updatedAt={matches.data?.my_location_updated_at}
            loading={busy || matches.isFetching}
            onClick={captureAndSave}
          />
        </div>
      </header>

      {matches.isLoading ? (
        <RadarSkeleton />
      ) : locationRequired ? (
        <EmptyEnable busy={busy} onEnable={captureAndSave} geoError={geoError} />
      ) : matches.isError ? (
        <p
          data-testid="radar-fetch-error"
          className="text-center text-[#FF6B6B] font-body text-sm py-12"
        >
          Couldn't load matches. Try refreshing.
        </p>
      ) : (
        <>
          {/* Tab strip */}
          <Tabs value={viewTab} onValueChange={setViewTab} className="w-full">
            <TabsList
              data-testid="radar-view-tabs"
              className="flex w-full h-auto p-0 bg-transparent border-b border-white/10 rounded-none"
            >
              <TabsTrigger
                value="all"
                data-testid="radar-tab-all"
                className="flex-1 pb-3 pt-1 text-sm font-medium bg-transparent rounded-none text-[#A1A1AA] data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-[#E5B83A] transition-all"
              >
                All ({matches.data?.matches?.length || 0})
              </TabsTrigger>
              <TabsTrigger
                value="saved"
                data-testid="radar-tab-saved"
                className="flex-1 pb-3 pt-1 text-sm font-medium bg-transparent rounded-none text-[#A1A1AA] data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-[#E5B83A] transition-all"
              >
                <Star className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />
                Saved ({saved.data?.length || 0})
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {rows.length === 0 ? (
            viewTab === "saved" ? (
              <EmptySavedList />
            ) : (
              <EmptyNoMatches
                busy={busy}
                onRefresh={() => matches.refetch()}
                geoError={geoError}
              />
            )
          ) : (
            <section data-testid="match-list" className="space-y-3">
              {rows.map((r) => (
                <MatchCard
                  key={r.user_id}
                  row={r}
                  isSaved={savedIds.has(r.user_id)}
                  onToggleSave={() => handleToggleSave(r.user_id, savedIds.has(r.user_id))}
                  onOpenChat={() => handleOpenChat(r.user_id)}
                  onOpenDetails={() => setActiveMatch(r)}
                  onOpenTrade={() =>
                    setTradeTarget({
                      user_id: r.user_id,
                      display_name: r.display_name,
                    })
                  }
                  chatPending={createThread.isPending}
                />
              ))}
            </section>
          )}
        </>
      )}

      <footer
        data-testid="radar-privacy-footer"
        className="text-[11px] text-[#A1A1AA]/60 font-body text-center pt-4 border-t border-white/5"
      >
        Only approximate distance and your display name are shown to other collectors.
        Your exact location is never shared.
      </footer>

      <MatchDetailsDialog
        open={!!activeMatch}
        onOpenChange={(open) => !open && setActiveMatch(null)}
        match={activeMatch}
      />

      <TradeGenerateModal
        open={!!tradeTarget}
        onOpenChange={(open) => !open && setTradeTarget(null)}
        target={tradeTarget}
      />

      <TradeScanModal
        open={scanOpen}
        onOpenChange={setScanOpen}
      />
    </div>
  );
}

/* ───── Saved-update toast text ───── */
function formatSavedUpdate(u) {
  if (u.type === "new") {
    return `🌟 ${u.display_name} is now a match! You give ${u.give_count}, you get ${u.get_count}.`;
  }
  if (u.type === "appeared") {
    return `${u.display_name} is back in range.`;
  }
  if (u.type === "grew") {
    return `${u.display_name} has new trades: +${u.give_delta} give, +${u.get_delta} get.`;
  }
  return u.display_name;
}

/* ───── Header chip ───── */
function LocationChip({ hasLocation, updatedAt, loading, onClick }) {
  if (!hasLocation) {
    return (
      <button
        type="button"
        data-testid="radar-location-chip"
        onClick={onClick}
        disabled={loading}
        className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-white/10 text-[#A1A1AA] font-body text-xs hover:bg-white/5 hover:border-white/30 disabled:opacity-40 transition-colors"
      >
        <MapPin className="h-3.5 w-3.5" />
        Location off · <span className="text-[#E5B83A]">Enable</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      data-testid="radar-location-chip"
      onClick={onClick}
      disabled={loading}
      className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-[#E5B83A]/30 bg-[#E5B83A]/5 text-white font-body text-xs hover:bg-[#E5B83A]/10 disabled:opacity-40 transition-colors"
    >
      <MapPin className="h-3.5 w-3.5 text-[#E5B83A]" />
      Updated {timeAgo(updatedAt)} ·{" "}
      <span className="inline-flex items-center gap-1 text-[#E5B83A]">
        {loading ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <RefreshCw className="h-3 w-3" />
        )}
        Refresh
      </span>
    </button>
  );
}

/* ───── Empty states ───── */
function EmptyEnable({ busy, onEnable, geoError }) {
  return (
    <section
      data-testid="radar-empty-no-location"
      className="bg-[#0A0A0A] border border-white/10 rounded-xl p-8 sm:p-12 text-center max-w-md mx-auto"
    >
      <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[#E5B83A] to-[#C9971C] mx-auto mb-5 flex items-center justify-center shadow-[0_8px_28px_-12px_rgba(229,184,58,0.55)]">
        <RadarIcon className="h-7 w-7 text-black" />
      </div>
      <h2 className="font-display text-xl font-bold text-white">Turn on your radar</h2>
      <p className="font-body text-sm text-[#A1A1AA] mt-3 leading-relaxed">
        We need your location to find nearby collectors. Stays private — only
        approximate distance is shown to others.
      </p>
      <button
        type="button"
        onClick={onEnable}
        disabled={busy}
        data-testid="radar-enable-button"
        className="mt-6 w-full h-11 rounded-md font-semibold text-sm tracking-wide bg-gradient-to-r from-[#E5B83A] to-[#C9971C] text-black hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all inline-flex items-center justify-center gap-2"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
        Enable location
      </button>
      {geoError ? (
        <p data-testid="radar-error" className="mt-4 text-xs text-[#FF6B6B] font-body">
          {geoError}
        </p>
      ) : null}
    </section>
  );
}

function EmptyNoMatches({ busy, onRefresh, geoError }) {
  return (
    <section
      data-testid="radar-empty-no-matches"
      className="bg-[#0A0A0A] border border-white/10 rounded-xl p-8 sm:p-10 text-center"
    >
      <RadarIcon className="h-7 w-7 mx-auto text-[#A1A1AA]" />
      <h2 className="font-display text-lg font-bold text-white mt-4">
        No mutual trades within 5 km yet
      </h2>
      <p className="font-body text-sm text-[#A1A1AA] mt-2 leading-relaxed max-w-md mx-auto">
        Add more duplicates and missing stickers, or check back when more
        collectors join nearby.
      </p>
      <button
        type="button"
        onClick={onRefresh}
        disabled={busy}
        data-testid="radar-refresh-button"
        className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-md border border-white/15 text-white font-body text-sm hover:bg-white/5 disabled:opacity-40 transition-colors"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        Refresh radar
      </button>
      {geoError ? (
        <p data-testid="radar-error" className="mt-4 text-xs text-[#FF6B6B] font-body">
          {geoError}
        </p>
      ) : null}
    </section>
  );
}

function EmptySavedList() {
  return (
    <section
      data-testid="radar-empty-saved"
      className="bg-[#0A0A0A] border border-white/10 rounded-xl p-8 sm:p-10 text-center"
    >
      <Star className="h-7 w-7 mx-auto text-[#A1A1AA]" />
      <h2 className="font-display text-lg font-bold text-white mt-4">
        No saved matches yet
      </h2>
      <p className="font-body text-sm text-[#A1A1AA] mt-2 leading-relaxed max-w-md mx-auto">
        Star a match from the All tab to be notified when their trades shift.
      </p>
    </section>
  );
}

/* ───── Match card ───── */
function MatchCard({ row, isSaved, onToggleSave, onOpenChat, onOpenDetails, onOpenTrade, chatPending }) {
  const { t } = useI18n();
  const initial = (row.display_name || "?").charAt(0).toUpperCase();
  const outOfRange = !row.is_current_match;
  return (
    <article
      data-testid={`match-card-${row.user_id}`}
      className={
        "bg-[#0A0A0A] border border-white/10 rounded-xl p-4 sm:p-5 hover:border-[#E5B83A]/30 transition-colors " +
        (outOfRange ? "opacity-70" : "")
      }
    >
      <header className="flex items-start gap-3">
        <span className="h-10 w-10 rounded-full bg-gradient-to-br from-[#E5B83A] to-[#C9971C] text-black font-bold flex items-center justify-center shrink-0">
          {initial}
        </span>
        <div
          className="flex-1 min-w-0 cursor-pointer"
          onClick={onOpenDetails}
        >
          <h3
            className="font-display text-base font-bold text-white truncate"
            data-testid={`match-name-${row.user_id}`}
          >
            {row.display_name}
          </h3>
          <p
            className="font-body text-xs text-[#A1A1AA]"
            data-testid={`match-distance-${row.user_id}`}
          >
            {outOfRange ? (
              <span className="italic">Out of range right now</span>
            ) : (
              `${formatDistance(row.distance_meters)} away`
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={onToggleSave}
          data-testid={`match-save-${row.user_id}`}
          aria-label={isSaved ? "Unsave match" : "Save match"}
          className="h-8 w-8 rounded-md flex items-center justify-center hover:bg-white/5 transition-colors"
        >
          <Star
            className={
              "h-4 w-4 transition-colors " +
              (isSaved
                ? "fill-[#E5B83A] text-[#E5B83A]"
                : "text-[#A1A1AA] hover:text-white")
            }
          />
        </button>
      </header>

      {!outOfRange ? (
        <div className="mt-4 space-y-2.5">
          <ChipLine
            label="You give →"
            numbers={row.give}
            total={row.give_count}
            variant="solid"
            testid={`match-give-${row.user_id}`}
          />
          <ChipLine
            label="You get  ←"
            numbers={row.get}
            total={row.get_count}
            variant="outline"
            testid={`match-get-${row.user_id}`}
          />
        </div>
      ) : null}

      <footer className="mt-4 pt-3 border-t border-white/5 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={onOpenChat}
          disabled={chatPending || outOfRange}
          data-testid={`match-chat-${row.user_id}`}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#E5B83A]/40 text-[#E5B83A] font-body text-xs hover:bg-[#E5B83A]/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {chatPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageCircle className="h-3.5 w-3.5" />}
          Open chat
        </button>
        <button
          type="button"
          onClick={onOpenTrade}
          disabled={outOfRange}
          data-testid={`match-trade-${row.user_id}`}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-gradient-to-r from-[#E5B83A] to-[#C9971C] text-black font-body font-semibold text-xs hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
        >
          <QrCode className="h-3.5 w-3.5" />
          {t("trade.silent_trade")}
        </button>
        <button
          type="button"
          onClick={onOpenDetails}
          data-testid={`match-details-${row.user_id}`}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-white/10 text-white font-body text-xs hover:bg-white/5 transition-colors ml-auto"
        >
          View details
        </button>
      </footer>
    </article>
  );
}

function ChipLine({ label, numbers, total, variant, testid }) {
  const visible = useMemo(() => numbers.slice(0, VISIBLE_CHIPS), [numbers]);
  const overflow = total - visible.length;
  return (
    <div className="flex items-start gap-2">
      <span className="font-body text-[11px] uppercase tracking-wider text-[#A1A1AA] pt-1.5 shrink-0 w-20">
        {label}
      </span>
      <div className="flex flex-wrap gap-1.5 min-w-0" data-testid={testid}>
        {visible.map((n) => (
          <span
            key={n}
            className={
              "h-7 min-w-[28px] px-2 rounded-md flex items-center justify-center text-xs font-medium font-body border " +
              (variant === "solid"
                ? "bg-[#E5B83A] text-black border-[#E5B83A]"
                : "bg-transparent text-[#E5B83A] border-[#E5B83A]")
            }
          >
            {n}
          </span>
        ))}
        {overflow > 0 ? (
          <span className="h-7 px-2 rounded-md flex items-center justify-center text-xs font-medium font-body border border-white/10 text-[#A1A1AA]">
            +{overflow} more
          </span>
        ) : null}
      </div>
    </div>
  );
}

function RadarSkeleton() {
  return (
    <div className="space-y-3" data-testid="radar-loading">
      {[0, 1].map((i) => (
        <div key={i} className="bg-white/5 rounded-xl h-32 animate-pulse" />
      ))}
    </div>
  );
}
